import { Link } from 'react-router-dom';
import pluralize from 'library/helpers/pluralize';
import { useState, useEffect, useMemo } from 'react';
import BatchConfirm from 'library/components/modal/batch-confirm';
import BatchUpdate from 'library/components/modal/batch-update';
import BatchAddWizard from 'library/components/batch-add-wizard';
import TagPicker from 'library/components/form/tag-picker';
import * as yup from 'yup';
import Input from 'library/components/form/input';
import PickRadioModal from 'library/components/modal/pick-radio';
import { TYPE } from 'store/browse-items/fixtures';
import useTagList from 'library/hooks/use-deferred-data/use-tag-list';
import useStorageList from 'library/hooks/use-deferred-data/use-storage-list';
import useManufacturerList from 'library/hooks/use-deferred-data/use-manufacturer-list';
import IconNames from '../../constants/icon-names';
import {
  Container,
  Header,
  List,
  ListItem,
  Icon,
} from './styles';
import { ACTIONS, LABEL } from './constants';

// demo
const tagsValidation = yup.object().shape({
  tags: yup.array().of(yup.mixed().required()).min(1),
});

const layersValidation = yup.object().shape({
  count: yup.number().required(),
});

const drawerValidation = yup.object().shape({
  storage: yup.array().of(yup.mixed().required()).default([]),
});

const localIdValidation = yup.object().shape({
  id: yup.mixed().required(),
});

const manufacturerValidation = yup.object().shape({
  manufacturer: yup.array().of(yup.mixed().required()).default([]),
});

const BatchSelect = ({
  type,
  data = [],
  onDeselectAll,
  onDeselect,
  onDelete,
  onAddToWishlist,
  onBatchAddProperties,
  deleteType = 'item',
  disabledActions = [],
  addConfig = {
    resetBrowseItems: true,
    resetWishlistItems: true,
  },
}) => {
  const listItems = ACTIONS[type];
  const ids = data.map(({ id }) => id);
  const label = LABEL[type];
  const [isAddToWishlistModalOpened, toggleAddToWishlistModal] = useState(false);
  const [isRemoveModalOpened, toggleRemoveModal] = useState(false);
  const [openedBatchAddModal, toggleBatchAddModal] = useState(null);
  const [isBatchPickModalOpened, toggleBatchPickModalOpened] = useState(false);
  const [isBatchAddItemsTypeOpened, toggleBatchAddItemsTypeOpened] = useState(null);
  const [pickModalValue, setPickModalValue] = useState(null);
  const {
    options: manufacturers, onQueryChange: onManufacturerQueryChange, labelMap: manufacturerLabelMap, isLoading: isManufacturerListLoading,
  } = useManufacturerList(
    false,
  );
  const manufacturersOptions = useMemo(() => manufacturers.map(({ value: id, text: name }) => ({ id, name })), [isManufacturerListLoading, manufacturerLabelMap]);
  const manufacturerLabelToIdsMapFormatted = useMemo(() => {
    const copy = JSON.parse(JSON.stringify(manufacturerLabelMap));
    const result = {};
    Object.entries(copy).forEach(([key, value]) => {
      result[value] = key;
    });
    return result;
  }, [isManufacturerListLoading, manufacturerLabelMap]);
  const { labelMap: tagsMap, options: tagsOptions, onQueryChange: onTagQueryChange } = useTagList(ids.length === 0);
  const {
    labelMap: storagesLabelMap, options: storages, onQueryChange: onStorageQueryChange, isLoading: isStorageListLoading,
  } = useStorageList(false);

  const storagesOptions = useMemo(() => storages.map(({ value: id, text: name }) => ({ id, name })), [isStorageListLoading, storagesLabelMap]);
  const storageLabelToIdsMapFormatted = useMemo(() => {
    const copy = JSON.parse(JSON.stringify(storagesLabelMap));
    const result = {};
    Object.entries(copy).forEach(([key, value]) => {
      result[value] = key;
    });
    return result;
  }, [isStorageListLoading, storagesLabelMap]);

  useEffect(() => {
    if (!ids.length) {
      if (isAddToWishlistModalOpened) {
        toggleAddToWishlistModal(false);
      }

      if (isRemoveModalOpened) {
        toggleRemoveModal(false);
      }

      if (pickModalValue) {
        setPickModalValue(null);
      }

      if (openedBatchAddModal) {
        toggleBatchAddModal(null);
      }

      if (isBatchPickModalOpened) {
        toggleBatchPickModalOpened(false);
      }

      if (isBatchAddItemsTypeOpened) {
        toggleBatchAddItemsTypeOpened(null);
      }

      if (isBatchAddItemsTypeOpened) {
        toggleBatchAddItemsTypeOpened(null);
      }
    }
  }, [ids.length]);

  if (!data.length) {
    return null;
  }

  const text = `You have selected ${pluralize(label, ids.length)}`;

  const handleAddToWishlistModalOpen = () => {
    toggleAddToWishlistModal(true);
  };

  const handleAddToWishlistModalClose = () => {
    toggleAddToWishlistModal(false);
  };

  const handleBatchAddModalOpen = (batchType) => {
    toggleBatchAddModal(batchType);
  };

  const handleBatchPickModalClose = () => {
    toggleBatchPickModalOpened(false);
  };

  const handleBatchAddModalClose = () => {
    toggleBatchAddModal(null);
  };

  const handleRemoveModalOpen = () => {
    toggleRemoveModal(true);
  };

  const handleDeleteSubmit = (...props) => {
    onDelete(...props);

    handleBatchAddModalClose();
  };
  const handleRemoveModalClose = () => {
    toggleRemoveModal(false);
  };

  const BATCH_NAMES_MAP = {
    [IconNames.TAG]: 'tags',
    [IconNames.LAYERS]: 'layers',
    [IconNames.DRAWER]: 'drawer',
    [IconNames.MANUFACTURER]: 'manufacturer',
  };

  const renderItem = ({ name, title }) => {
    // todo - remove usage from here - demo only purpose
    if (name === IconNames.HEART) {
      return (
        <ListItem actionType={name} key={name} onClick={handleAddToWishlistModalOpen} title={title}>
          <Icon className={`icon-${name}`} />
        </ListItem>
      );
    }

    if (name === IconNames.TRASH) {
      return (
        <ListItem actionType={name} key={name} onClick={handleRemoveModalOpen} title={title}>
          <Icon className={`icon-${name}`} />
        </ListItem>
      );
    }

    if ([IconNames.TAG, IconNames.LAYERS, IconNames.DRAWER, IconNames.MANUFACTURER].includes(name)) {
      const handleOpen = () => {
        handleBatchAddModalOpen(BATCH_NAMES_MAP[name]);
      };

      return (
        <ListItem actionType={name} key={name} onClick={handleOpen} title={title}>
          <Icon className={`icon-${name}`} />
        </ListItem>
      );
    }

    if ([IconNames.SET, IconNames.PROJECTS].includes(name)) {
      const handleOpen = () => {
        toggleBatchAddItemsTypeOpened(name === IconNames.SET ? TYPE.SET : TYPE.PROJECT);
      };

      return (
        <ListItem actionType={name} key={name} onClick={handleOpen} title={title}>
          <Icon className={`icon-${name}`} />
        </ListItem>
      );
    }

    return (
      <ListItem actionType={name} key={name} disabled title={title}>
        <Link to="/">
          <Icon className={`icon-${name}`} />
        </Link>
      </ListItem>
    );
  };

  const getDeleteModalLabels = () => {
    switch (deleteType) {
    case 'wishlist-item': {
      return {
        title: `Remove ${data.length} items from wishlist`,
        text: `You are about to remove ${data.length} selected items from wishlist, are you sure?`,
        submitText: `Remove ${data.length} items from wishlist`,
      };
    }
    case 'set-item': {
      return {
        title: `Remove ${data.length} items from set`,
        text: `You are about to remove ${data.length} selected items from set, are you sure?`,
        submitText: `Remove ${data.length} items from set`,
      };
    }
    case 'set': {
      return {
        title: `Delete ${data.length} sets`,
        text: `You are about to delete ${data.length} selected sets, are you sure?`,
        submitText: `Delete ${data.length} sets`,
      };
    }
    case 'item': {
      return {
        title: `Delete ${data.length} items`,
        text: `You are about to delete ${data.length} selected items, are you sure?`,
        submitText: `Delete ${data.length} items`,
      };
    }
    case 'project-item': {
      return {
        title: `Remove ${data.length} items from project`,
        text: `You are about to remove ${data.length} selected items from project, are you sure?`,
        submitText: `Remove ${data.length} items from project`,
      };
    }
    case 'project-set': {
      return {
        title: `Remove ${data.length} sets from project`,
        text: `You are about to remove ${data.length} selected sets from project, are you sure?`,
        submitText: `Remove ${data.length} sets from project`,
      };
    }
    case 'project-color': {
      return {
        title: `Remove ${data.length} colors from project`,
        text: `You are about to remove ${data.length} selected colors from project, are you sure?`,
        submitText: `Remove ${data.length} colors from project`,
      };
    }
    case 'project-image': {
      return {
        title: `Remove ${data.length} images from project`,
        text: `You are about to remove ${data.length} selected images from project, are you sure?`,
        submitText: `Remove ${data.length} images from project`,
      };
    }
    }

    return null;
  };

  const PICK_FIELDS_SCHEMA = {
    drawer: {
      name: 'id',
      isAddEnabled: true,
      title: 'Pick a storage',
      placeholder: 'Search for a storage',
      onSubmit: ({ id }) => {
        setPickModalValue(id);

        handleBatchPickModalClose();
      },
      validation: localIdValidation,
      options: storages,
      onSearch: onStorageQueryChange,
    },
    manufacturer: {
      name: 'id',
      title: 'Pick a manufacturer',
      isAddEnabled: true,
      placeholder: 'Search for a manufacturer',
      onSubmit: ({ id }) => {
        setPickModalValue(id);

        handleBatchPickModalClose();
      },
      validation: localIdValidation,
      options: manufacturers,
      onSearch: onManufacturerQueryChange,
    },
  };

  const BATCH_SCHEMA = {
    tags: {
      text: 'You are about to change tag value for selected items:',
      submitText: 'Add new tags',
      label: 'Add new tags below:',
      hint: 'Your existing tags will remain, your new tags will be appended to the existing ones.',
      title: `Edit tags for ${data.length} items`,
      name: 'tags',
      validation: tagsValidation,
      fieldProps: {
        component: TagPicker,
        options: tagsOptions,
        formatterMap: tagsMap,
        placeholder: 'Add tags',
        onQueryChange: onTagQueryChange,
        isFocusedOnMount: true,
      },
    },
    layers: {
      text: 'You are about to change quantity value for selected items:',
      submitText: 'Save changes',
      label: 'Edit quantity below:',
      hint: 'New quantity value will replace the existing ones.',
      title: `Edit quantity for ${data.length} items`,
      name: 'count',
      validation: layersValidation,
      fieldProps: {
        component: Input,
        placeholder: 'Edit quantity',
        type: 'number',
        min: 1,
        iconClassName: 'icon-layers',
        isFocusedOnMount: true,
      },
    },
    drawer: {
      text: 'You are about to change storage location value for selected items:',
      submitText: 'Save changes',
      label: 'Edit storage location below:',
      hint: 'New storage location value will replace the existing ones.',
      title: `Edit storage location for ${data.length} items`,
      name: 'storage',
      validation: drawerValidation,
      fieldProps: {
        component: TagPicker,
        options: storagesOptions,
        formatterMap: storagesLabelMap,
        nameMap: storageLabelToIdsMapFormatted,
        placeholder: 'Empty value',
        onQueryChange: onStorageQueryChange,
        isFocusedOnMount: true,
        isSingleOption: true,
        minQueryLength: 1,
        icon: 'icon-drawer',
      },
    },
    manufacturer: {
      text: 'You are about to change manufacturer value for selected items:',
      submitText: 'Save changes',
      label: 'Edit manufacturer value:',
      hint: 'New manufacturer value will replace the existing ones.',
      title: `Edit manufacturer for ${data.length} items`,
      name: 'manufacturer',
      validation: manufacturerValidation,
      fieldProps: {
        component: TagPicker,
        options: manufacturersOptions,
        formatterMap: manufacturerLabelMap,
        nameMap: manufacturerLabelToIdsMapFormatted,
        placeholder: 'Empty value',
        onQueryChange: onManufacturerQueryChange,
        isFocusedOnMount: true,
        isSingleOption: true,
        minQueryLength: 1,
        icon: 'icon-manufacturer',
      },
    },
  };

  return (
    <Container type={type}>
      <Header>
        {text}
        <span onClick={onDeselectAll}>&nbsp;/&nbsp;De-select all</span>
      </Header>
      <List>
        {listItems.filter(({ name }) => !disabledActions.includes(name)).map(renderItem)}
      </List>
      <BatchConfirm
        title={`Add ${data.length} items to wishlist`}
        text={`You are about to add ${data.length} selected items to wishlist, are you sure?`}
        isOpened={isAddToWishlistModalOpened}
        onClose={handleAddToWishlistModalClose}
        onDeselect={onDeselect}
        onSubmit={onAddToWishlist}
        data={data}
        submitText={`Yes, add ${data.length} items to wishlist`}
      />
      <BatchConfirm
        {...getDeleteModalLabels()}
        isOpened={isRemoveModalOpened}
        onClose={handleRemoveModalClose}
        onDeselect={onDeselect}
        onSubmit={handleDeleteSubmit}
        data={data}
        type={type}
      />
      {
        (openedBatchAddModal && !isBatchPickModalOpened)
          ? (
            <BatchUpdate
              {...BATCH_SCHEMA[openedBatchAddModal]}
              isOpened
              onClose={handleBatchAddModalClose}
              onDeselect={onDeselect}
              onSubmit={(newData) => {
                onBatchAddProperties(newData, openedBatchAddModal);
                handleBatchAddModalClose(true);
              }}
              data={data}
            />
          ) : null
      }
      {
        !pickModalValue ? (
          <PickRadioModal
            {...PICK_FIELDS_SCHEMA[openedBatchAddModal]}
            text=""
            submitText="Save changes"
            cancelText="Cancel"
            overlayClassName="Overlay"
            cancelWidth="94px"
            isOpened={!!isBatchPickModalOpened}
            onClose={handleBatchPickModalClose}
          />
        ) : null
      }
      {
        isBatchAddItemsTypeOpened
          ? (
            <BatchAddWizard
              type={isBatchAddItemsTypeOpened}
              entityType={type}
              data={data}
              onDeselect={onDeselect}
              onClose={() => toggleBatchAddItemsTypeOpened(null)}
              config={addConfig}
            />
          ) : null
      }
    </Container>
  );
};

export default BatchSelect;
